
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { formatPhoneNumber } from '@/utils/phoneValidation';
import { logSecurityEvent, validatePhoneNumber, sanitizeInput } from '@/utils/securityAudit';
import { useRateLimit } from './useRateLimit';

export interface VerificationState {
  loading: boolean;
  verified: boolean;
  phoneNumber: string;
  userExists: boolean;
  error: string | null;
  otpRequested: boolean;
  otpSent: boolean;
}

export interface VerificationResult {
  success: boolean;
  credentials?: {
    email: string;
    password: string;
  };
}

export const usePhoneVerification = () => {
  const { toast } = useToast();
  const { checkRateLimit } = useRateLimit();
  
  const checkUserExists = useCallback(async (phoneNumber: string) => {
    try {
      const sanitizedPhone = sanitizeInput(phoneNumber);
      
      if (!validatePhoneNumber(sanitizedPhone)) {
        throw new Error('Invalid phone number format');
      }

      const formattedPhoneNumber = formatPhoneNumber(sanitizedPhone);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('phone_number', formattedPhoneNumber)
        .maybeSingle();

      if (error) {
        await logSecurityEvent({
          event_type: 'USER_CHECK_FAILED',
          details: { error: error.message, phone: formattedPhoneNumber }
        });
        throw new Error(`Error checking user: ${error.message}`);
      }

      return !!data;
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to check user status',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  const requestOTP = useCallback(async (phoneNumber: string) => {
    try {
      const sanitizedPhone = sanitizeInput(phoneNumber);
      
      if (!validatePhoneNumber(sanitizedPhone)) {
        throw new Error('Invalid phone number format');
      }

      const formattedPhoneNumber = formatPhoneNumber(sanitizedPhone);
      
      // Check rate limiting
      const isBlocked = await checkRateLimit({
        identifier: formattedPhoneNumber,
        action: 'OTP_REQUEST',
        maxAttempts: 3,
        windowMinutes: 15
      });

      if (isBlocked) {
        throw new Error('Too many OTP requests. Please try again later.');
      }

      // First check if user exists to ensure the correct verification flow
      await checkUserExists(formattedPhoneNumber);
      
      const { error } = await supabase.functions.invoke('generate-otp', {
        body: { phoneNumber: formattedPhoneNumber }
      });

      if (error) {
        await logSecurityEvent({
          event_type: 'OTP_REQUEST_FAILED',
          details: { error: error.message, phone: formattedPhoneNumber }
        });
        throw new Error(`Error requesting OTP: ${error.message}`);
      }

      await logSecurityEvent({
        event_type: 'OTP_REQUESTED',
        details: { phone: formattedPhoneNumber }
      });

      toast({
        title: 'OTP Sent',
        description: 'A verification code has been sent to your phone',
      });

      return true;
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send verification code',
        variant: 'destructive',
      });
      
      throw error;
    }
  }, [checkUserExists, toast, checkRateLimit]);

  const verifyOTP = useCallback(async (otp: string, phoneNumber: string): Promise<VerificationResult> => {
    try {
      const sanitizedOTP = sanitizeInput(otp);
      const sanitizedPhone = sanitizeInput(phoneNumber);
      
      if (!sanitizedOTP || sanitizedOTP.length !== 6 || !/^\d{6}$/.test(sanitizedOTP)) {
        throw new Error('Invalid OTP format');
      }
      
      if (!validatePhoneNumber(sanitizedPhone)) {
        throw new Error('Invalid phone number format');
      }
      
      const formattedPhoneNumber = formatPhoneNumber(sanitizedPhone);

      // Check rate limiting for OTP verification
      const isBlocked = await checkRateLimit({
        identifier: formattedPhoneNumber,
        action: 'OTP_VERIFY',
        maxAttempts: 5,
        windowMinutes: 15
      });

      if (isBlocked) {
        await logSecurityEvent({
          event_type: 'OTP_VERIFY_RATE_LIMITED',
          details: { phone: formattedPhoneNumber }
        });
        throw new Error('Too many verification attempts. Please try again later.');
      }

      const { data, error } = await supabase.functions.invoke('verify-otp', {
        body: { 
          phoneNumber: formattedPhoneNumber,
          otpCode: sanitizedOTP 
        }
      });

      if (error) {
        await logSecurityEvent({
          event_type: 'OTP_VERIFY_FAILED',
          details: { error: error.message, phone: formattedPhoneNumber }
        });
        throw new Error(`Error verifying OTP: ${error.message}`);
      }

      if (!data?.success) {
        await logSecurityEvent({
          event_type: 'OTP_VERIFY_INVALID',
          details: { phone: formattedPhoneNumber }
        });
        throw new Error(data?.error || 'Invalid verification code');
      }

      await logSecurityEvent({
        event_type: 'OTP_VERIFIED_SUCCESS',
        details: { phone: formattedPhoneNumber }
      });

      toast({
        title: 'Verification Successful',
        description: 'You have been successfully verified',
      });

      return {
        success: true,
        credentials: data.credentials
      };
    } catch (error) {
      toast({
        title: 'Verification Failed',
        description: error instanceof Error ? error.message : 'Failed to verify code',
        variant: 'destructive',
      });
      
      throw error;
    }
  }, [toast, checkRateLimit]);

  const resetState = useCallback(() => {
    // Reset function for clearing state
  }, []);

  return {
    verificationState: {
      loading: false,
      verified: false,
      phoneNumber: '',
      userExists: false,
      error: null,
      otpRequested: false,
      otpSent: false,
    },
    requestOTP,
    verifyOTP,
    resetState
  };
};

export default usePhoneVerification;
